import type { ReviewStats } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const MONTHS = 12

function monthLabel(month: string): string {
  const m = Number(month.slice(5, 7))
  return ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][m - 1] ?? '·'
}

/**
 * Reviews that came in below five, month by month, against that month's
 * total volume.
 *
 * `monthly[].sub_five` has been computed nightly all along and never shown.
 * The rating line next to this answers "what is the average", which a single
 * three-star review barely moves in a busy month — this answers "how many
 * guests were actually unhappy", which is the number that drives the
 * sub-rating work.
 *
 * A rolling twelve months rather than a calendar year, so the shape does not
 * collapse every January.
 */
export function SubFiveTrend({ stats }: { stats: ReviewStats }) {
  const months = stats.monthly.slice(-MONTHS)
  if (months.length < 2) return null

  const total = months.reduce((n, m) => n + m.sub_five, 0)
  const busiest = Math.max(1, ...months.map(m => m.reviews))
  const worst = Math.max(...months.map(m => m.sub_five))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Unhappy guests by month</CardTitle>
        <CardDescription>
          Reviews below five, in colour, against that month&apos;s total review volume. {total} in
          the last {months.length} months.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex h-32 items-end gap-1.5 border-b border-border pb-0">
          {months.map(m => {
            // Both bars share the volume scale so the solid one reads as a
            // share of the month, not a free-floating count — three bad
            // reviews in a 40-review month is a different story from three
            // in a month of five.
            const volPct = (m.reviews / busiest) * 100
            const badPct = (m.sub_five / busiest) * 100
            return (
              <Tooltip key={m.month}>
                <TooltipTrigger asChild>
                  <div
                    tabIndex={0}
                    className="flex flex-1 cursor-default flex-col items-center gap-1 focus-visible:outline-hidden"
                  >
                    <div className="relative flex h-28 w-full items-end">
                      <div
                        className="absolute bottom-0 w-full rounded-sm bg-surface2"
                        style={{ height: `${Math.max(2, volPct)}%` }}
                      />
                      {m.sub_five > 0 && (
                        <div
                          className={`absolute bottom-0 w-full rounded-sm ${
                            m.sub_five === worst && worst > 1 ? 'bg-danger' : 'bg-amber'
                          }`}
                          style={{ height: `${Math.max(2, badPct)}%` }}
                        />
                      )}
                    </div>
                    <span className="text-[10px] text-textMuted">{monthLabel(m.month)}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {m.month}: {m.sub_five} below five of {m.reviews} review
                  {m.reviews === 1 ? '' : 's'}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
